/**
 * hero-collection-analytics.js
 *
 * GA4 event wiring for the hero collection template (templates/collection.hero.json).
 *
 * Fires events to window.dataLayer using the same pattern as
 * segment-controller.js and postcode-checker.js:
 *   - view_hero_collection          (on page load)
 *   - click_fulfillment_option      (delivery | pickup | shipping)
 *   - click_featured_product        (product_id)
 *   - click_educational_content     (blog_link + article handle)
 *   - view_terrain_teaser           (lawn/garden only, IntersectionObserver)
 *   - view_sentinel_teaser          (livestock only, IntersectionObserver)
 *
 * Scroll-depth and enhanced-ecommerce continue to fire through existing
 * Shopify / theme instrumentation. This file adds page-specific events only.
 *
 * Self-initialises once per page. Safe to include multiple times (idempotent).
 */

(function () {
  'use strict';

  if (window.__hcAnalyticsInit) {
    return;
  }
  window.__hcAnalyticsInit = true;

  window.dataLayer = window.dataLayer || [];

  var push = function (eventName, params) {
    try {
      var payload = Object.assign({ event: eventName }, params || {});
      window.dataLayer.push(payload);
    } catch (err) {
      if (window.console && console.warn) {
        console.warn('[hero-collection-analytics] push failed', err);
      }
    }
  };

  var getCollectionKey = function () {
    var el = document.querySelector('[data-ga4-collection]');
    if (el && el.dataset.ga4Collection) {
      return el.dataset.ga4Collection;
    }
    // Fallback: derive from path
    var m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return m ? m[1] : '';
  };

  var init = function () {
    var collectionKey = getCollectionKey();

    // Only fire hero-collection events when we recognise a hero collection page.
    // The fulfillment section is exclusive to this template, so its presence
    // acts as the page-type gate.
    var fulfillmentSection = document.querySelector('.hero-collection-fulfillment');
    if (!fulfillmentSection) {
      return;
    }

    // --- 1. Page view
    push('view_hero_collection', {
      collection_name: collectionKey,
      page_location: window.location.href
    });

    // --- 2. Fulfillment tile clicks
    var fulfillmentTiles = document.querySelectorAll('.js-hc-fulfillment-option');
    fulfillmentTiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        push('click_fulfillment_option', {
          option: tile.dataset.fulfillmentOption || '',
          collection_name: collectionKey
        });
      });
    });

    // --- 3. Featured product card clicks (inside hero-collection-top-sellers section)
    var featuredSection = document.querySelector('.hero-collection-top-sellers');
    if (featuredSection) {
      featuredSection.addEventListener('click', function (evt) {
        var link = evt.target.closest('a[href*="/products/"]');
        if (!link || !featuredSection.contains(link)) {
          return;
        }
        var productMatch = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        var productId = link.dataset.productId
          || link.dataset.product
          || (productMatch ? productMatch[1] : '');
        push('click_featured_product', {
          product_id: productId,
          collection_name: collectionKey
        });
      });
    }

    // --- 4. Educational (blog) card clicks
    var blogCards = document.querySelectorAll('.js-hc-blog-card');
    blogCards.forEach(function (card) {
      card.addEventListener('click', function () {
        push('click_educational_content', {
          content_type: 'blog_link',
          content_id: card.dataset.articleHandle || '',
          collection_name: collectionKey
        });
      });
    });

    // --- 5 & 6. TERRAIN / Sentinel teaser view tracking
    var teaserEl = document.querySelector('[data-teaser="terrain"], [data-teaser="sentinel"]');
    if (teaserEl && 'IntersectionObserver' in window) {
      var observer = new IntersectionObserver(function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            var kind = entry.target.dataset.teaser; // "terrain" or "sentinel"
            push('view_' + kind + '_teaser', {
              collection_name: collectionKey
            });
            obs.unobserve(entry.target);
          }
        });
      }, { threshold: 0.5 });
      observer.observe(teaserEl);

      // Also track clicks on the teaser CTA
      var teaserCta = teaserEl.querySelector('.js-hc-teaser-cta');
      if (teaserCta) {
        teaserCta.addEventListener('click', function () {
          push('click_' + (teaserCta.dataset.teaser || 'teaser') + '_cta', {
            collection_name: collectionKey
          });
        });
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
